import React, { useMemo, useState } from 'react';
import { useApp } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import ItemDetailModal from '../components/ItemDetailModal';
import AddItemDialog from '../components/AddItemDialog';
import {
  ChevronDown,
  ChevronRight,
  Plus,
  CheckCircle2,
  CircleDot,
  ImageOff,
  Search,
  X,
  Pencil,
  History,
  Printer,
  Rows3,
} from 'lucide-react';
import { useOutletContext, useSearchParams } from 'react-router-dom';

function StatusBadge({ status }) {
  if (status === 'available')
    return (
      <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
        <CheckCircle2 size={11} /> Available
      </span>
    );
  if (status === 'checked-out')
    return (
      <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-rose-500/10 text-rose-400 border border-rose-500/20">
        <CircleDot size={11} /> Checked Out
      </span>
    );
  if (status === 'reserved')
    return (
      <span
        className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-yellow-400/10 text-yellow-300 border border-yellow-400/30"
        title="Reserved on an active project but not yet packed"
        data-testid="status-reserved"
      >
        <CircleDot size={11} /> Reserved
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20">
      <CircleDot size={11} /> Partial
    </span>
  );
}

function Thumb({ src, name }) {
  const [err, setErr] = useState(false);
  if (!src || err) {
    return (
      <div className="h-8 w-12 rounded bg-neutral-900 border border-neutral-800 grid place-items-center text-neutral-600">
        <ImageOff size={12} />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={name}
      className="h-8 w-12 object-cover rounded border border-neutral-800"
      onError={() => setErr(true)}
    />
  );
}

export default function Inventory() {
  const { items, itemStatus, itemAvailableQty, categories: CATEGORIES, categoryColor } = useApp();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const ctx = useOutletContext() || {};
  const urlQ = searchParams.get('q') || '';
  const urlCategory = searchParams.get('category') || '';
  const [localQ, setLocalQ] = useState(urlQ);
  const search = (ctx.globalSearch || localQ || urlQ || '').toLowerCase().trim();

  const [collapsed, setCollapsed] = useState({});
  const [selected, setSelected] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [activeCategory, setActiveCategory] = useState(urlCategory || 'All');
  // Compact mode: hides Location + Actions columns and slims widths so the
  // table fits natively on iPad-ish widths without horizontal scrolling.
  // Persisted so the setting survives reloads.
  const [compact, setCompact] = useState(
    () => localStorage.getItem('inv:compact') === '1',
  );
  React.useEffect(() => {
    localStorage.setItem('inv:compact', compact ? '1' : '0');
  }, [compact]);

  const gridCols = compact
    ? 'grid-cols-[40px_60px_110px_minmax(160px,1fr)_140px_110px_90px]'
    : 'grid-cols-[40px_60px_110px_minmax(220px,1fr)_180px_140px_110px_100px_100px]';
  // Base bg for sticky cells so scrolled content doesn't bleed through.
  const stickyBase = 'sticky z-[5] bg-[#131313] group-hover:bg-[#181818]';
  const stickyHead = 'sticky z-[6] bg-[#111]';

  // Keep local pill in sync with URL param when arriving via deep-link
  React.useEffect(() => {
    if (urlCategory && urlCategory !== activeCategory) {
      setActiveCategory(urlCategory);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlCategory]);

  const filtered = useMemo(() => {
    return items.filter((it) => {
      if (activeCategory !== 'All' && it.category !== activeCategory) return false;
      if (search) {
        const hay = `${it.code} ${it.name} ${it.category}`.toLowerCase();
        if (!hay.includes(search)) return false;
      }
      return true;
    });
  }, [items, activeCategory, search]);

  const groups = useMemo(() => {
    const map = new Map();
    CATEGORIES.forEach((c) => map.set(c.name, []));
    filtered.forEach((it) => {
      if (!map.has(it.category)) map.set(it.category, []);
      map.get(it.category).push(it);
    });
    return Array.from(map.entries()).filter(([, arr]) => arr.length > 0);
  }, [filtered]);

  const totalUnits = filtered.reduce((s, i) => s + i.totalQty, 0);

  return (
    <div className="p-5">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-white">Main Inventory</h1>
          <p className="text-xs text-neutral-500 mt-0.5">
            {filtered.length} items · {totalUnits} units · grouped by category
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input
              value={localQ}
              onChange={(e) => {
                setLocalQ(e.target.value);
                if (e.target.value) setSearchParams({ q: e.target.value });
                else setSearchParams({});
              }}
              placeholder="Filter…"
              className="bg-neutral-950 border border-neutral-800 text-white text-xs rounded-md pl-8 pr-7 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500/60 w-48"
            />
            {localQ && (
              <button
                onClick={() => {
                  setLocalQ('');
                  setSearchParams({});
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white"
              >
                <X size={12} />
              </button>
            )}
          </div>
          <button
            onClick={() => setCompact((v) => !v)}
            className={`text-[11px] px-2.5 py-1.5 rounded-md border flex items-center gap-1.5 ${
              compact
                ? 'border-amber-500/40 text-amber-300 bg-amber-500/10'
                : 'border-neutral-800 text-neutral-400 hover:bg-neutral-900'
            }`}
            title={compact ? 'Show all columns' : 'Compact mode — hides Location + Actions'}
            data-testid="inventory-compact-toggle"
          >
            <Rows3 size={13} /> {compact ? 'Compact' : 'Compact'}
          </button>
          <button
            onClick={() => {
              setEditingItem(null);
              setAddOpen(true);
            }}
            className="btn-amber rounded-md px-3 py-1.5 text-xs flex items-center gap-2"
          >
            <Plus size={14} /> Add Item
          </button>
        </div>
      </div>

      {/* Category chips */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        <button
          onClick={() => setActiveCategory('All')}
          className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
            activeCategory === 'All'
              ? 'bg-amber-500/15 border-amber-500/40 text-amber-300'
              : 'border-neutral-800 text-neutral-400 hover:bg-neutral-900'
          }`}
        >
          All ({items.length})
        </button>
        {CATEGORIES.map((c) => {
          const count = items.filter((i) => i.category === c.name).length;
          if (count === 0) return null;
          const active = activeCategory === c.name;
          return (
            <button
              key={c.name}
              onClick={() => setActiveCategory(c.name)}
              className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors flex items-center gap-1.5 ${
                active
                  ? 'border-amber-500/40 text-amber-300 bg-amber-500/10'
                  : 'border-neutral-800 text-neutral-400 hover:bg-neutral-900'
              }`}
            >
              <span className="cat-dot" style={{ background: c.color }} />
              {c.name} ({count})
            </button>
          );
        })}
      </div>

      {/* Table */}
      <div className="surface rounded-lg overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
        <div className={compact ? '' : 'min-w-[1060px]'}>
        <div className={`grid ${gridCols}`}>
          <div className={`head-cell ${stickyHead}`} style={{ left: 0 }}>#</div>
          <div className={`head-cell ${stickyHead}`} style={{ left: 40 }}>Photo</div>
          <div className={`head-cell ${stickyHead}`} style={{ left: 100 }}>Code</div>
          <div className="head-cell">Name</div>
          <div className="head-cell">Category</div>
          {!compact && <div className="head-cell">Location</div>}
          <div className="head-cell">Status</div>
          <div className="head-cell text-right">Available</div>
          {!compact && <div className="head-cell text-right">Actions</div>}
        </div>

        {groups.map(([cat, rows]) => {
          const isCollapsed = collapsed[cat];
          const total = rows.reduce((s, r) => s + r.totalQty, 0);
          return (
            <div key={cat}>
              <button
                onClick={() => setCollapsed((c) => ({ ...c, [cat]: !c[cat] }))}
                className="group-header w-full flex items-center px-4 py-2 text-left text-sm hover:bg-neutral-900"
              >
                {isCollapsed ? (
                  <ChevronRight size={14} className="text-neutral-500 mr-2" />
                ) : (
                  <ChevronDown size={14} className="text-neutral-500 mr-2" />
                )}
                <span className="cat-dot mr-2" style={{ background: categoryColor(cat) }} />
                <span className="text-neutral-100 font-medium">{cat}</span>
                <span className="ml-2 text-[11px] text-neutral-500">
                  {rows.length} · {total} units
                </span>
              </button>
              {!isCollapsed &&
                rows.map((it, idx) => {
                  const status = itemStatus(it.code);
                  const avail = itemAvailableQty(it.code);
                  return (
                    <div
                      key={it.id}
                      className={`grid ${gridCols} row-hover border-b border-neutral-900 cursor-pointer group`}
                      onClick={() => setSelected(it)}
                    >
                      <div className={`cell text-neutral-500 ${stickyBase}`} style={{ left: 0 }}>{idx + 1}</div>
                      <div className={`cell ${stickyBase}`} style={{ left: 40 }}>
                        <Thumb src={it.image} name={it.name} />
                      </div>
                      <div className={`cell text-neutral-300 font-mono text-[12px] ${stickyBase}`} style={{ left: 100 }}>{it.code}</div>
                      <div className="cell text-white">{it.name}</div>
                      <div className="cell">
                        <span className="inline-flex items-center gap-1.5 text-[11px]">
                          <span className="cat-dot" style={{ background: categoryColor(it.category) }} />
                          <span className="text-neutral-300">{it.category}</span>
                        </span>
                      </div>
                      {!compact && <div className="cell text-neutral-400">{it.location}</div>}
                      <div className="cell">
                        <StatusBadge status={status} />
                      </div>
                      <div className="cell text-right text-neutral-200">
                        <span className="text-white font-medium">{avail}</span>
                        <span className="text-neutral-600"> / {it.totalQty}</span>
                      </div>
                      {!compact && (
                      <div className="cell text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => navigate(`/inventory/${it.code}`)}
                            title="History"
                            className="h-6 w-6 grid place-items-center rounded text-neutral-400 hover:bg-neutral-900 hover:text-amber-400"
                          >
                            <History size={11} />
                          </button>
                          <button
                            onClick={() =>
                              window.open(
                                `/print-label/item/${it.code}`,
                                '_blank',
                                'width=540,height=620',
                              )
                            }
                            title="Print QR label"
                            data-testid={`print-label-item-${it.code}`}
                            className="h-6 w-6 grid place-items-center rounded text-neutral-400 hover:bg-neutral-900 hover:text-amber-400"
                          >
                            <Printer size={11} />
                          </button>
                          <button
                            onClick={() => {
                              setEditingItem(it);
                              setAddOpen(true);
                            }}
                            title="Edit item"
                            className="h-6 w-6 grid place-items-center rounded text-neutral-400 hover:bg-neutral-900 hover:text-amber-400"
                          >
                            <Pencil size={11} />
                          </button>
                        </div>
                      </div>
                      )}
                    </div>
                  );
                })}
            </div>
          );
        })}

        {groups.length === 0 && (
          <div className="p-12 text-center text-sm text-neutral-500">
            No items match your filters.
          </div>
        )}
        </div>
      </div>

      <ItemDetailModal item={selected} onClose={() => setSelected(null)} />
      <AddItemDialog
        open={addOpen}
        onClose={() => {
          setAddOpen(false);
          setEditingItem(null);
        }}
        editingItem={editingItem}
      />
    </div>
  );
}
